import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { AppContext } from "../util/AppContext";
import { getConfigValue, isBatchPrintEnabledForAnyProduct } from "../util/getConfigValue";
import { useRoutingValidation } from "../util/useRoutingValidation";
import { getMarketsForSellSheetGeneration } from "../util/getMarkets"
import { useBreadcrumbs } from "../util/BreadcrumbContext";
import CreateSalesAidStepProgressBar, { SalesAidStepIds } from "../components/CreateSalesAidStepProgressBar";
import { focusTypeName } from "../util/constants";

function TerritoryPage() {
  const [appState, setAppState] = useContext(AppContext);

  const [marketList, setMarketList] = useState();
  const [territoryList, setTerritoryList] = useState();
  const [batchPrintTerritories, setBatchPrintTerritories] = useState([]);

  const [multipleMarkets, setMultipleMarkets] = useState(false);

  const [selectedMarket, setSelectedMarket] = useState(appState.market || null);
  const [selectedTerritory, setSelectedTerritory] = useState(
    appState.territory || null
  );

  const useStickyNavigation = getConfigValue(appState.config.CreateSalesAid?.Features?.StickyNavigation?.Enabled, appState)?.Value === 'true';
  const { setBreadcrumbs } = useBreadcrumbs();

  // Set default focus to HCPs if not already set
  useEffect(() => {
    if (!appState.focus) {
      setAppState((prev) => ({
        ...prev,
        focus: focusTypeName.HCPs,
      }));
    }
  }, [appState.focus, setAppState]);

  useEffect(() => {
    setBreadcrumbs([
      { label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: "/FocusPage" }
    ]);
  }, [setBreadcrumbs]);

  useRoutingValidation();

  let selectMarketConfigValue = getConfigValue(
    appState.config?.Territory.SelectMarket,
    appState
  )?.Value;
  let selectTerritoryConfigValue = getConfigValue(
    appState.config?.Territory.SelectTerritory,
    appState
  )?.Value;
  const batchPrintEnabled = useMemo(
    function () {
      return (
        isBatchPrintEnabledForAnyProduct(appState)
      );
    },
    [appState]
  );

  const handleMarket = useCallback(
    function (market) {
      setAppState(prev => ({
        ...prev,
        market: market,
      }));

      setSelectedMarket(market);
    },
    [setAppState, setSelectedMarket]
  );

  const handleTerritory = useCallback(
    function (territory) {
      setAppState(prev => ({
        ...prev,
        territory: territory,
      }));

      setSelectedTerritory(territory);
    },
    [setAppState, setSelectedTerritory]
  );

  useEffect(() => {
    let markets = JSON.parse(sessionStorage.getItem("marketsForUser"));
    markets = getMarketsForSellSheetGeneration(appState?.sellSheetProductDetails, markets);

    if (Object.keys(markets)?.length > 1) {
      setMultipleMarkets(true);
    } else {
      handleMarket(markets[0]);
    }

    setMarketList(markets);

    if (batchPrintEnabled) {
      const territories = JSON.parse(
        sessionStorage.getItem("batchPrintTerritories")
      );
      setBatchPrintTerritories(territories);
    }
  }, [handleMarket, batchPrintEnabled]);

  useEffect(() => {
    if (batchPrintEnabled && appState.focus === focusTypeName.Messages && appState.territory?.id && !batchPrintTerritories.includes(appState.territory.id)) {
      handleTerritory(null);
    }
  }, [appState.focus, appState.territory, handleTerritory, batchPrintEnabled, batchPrintTerritories]);

  useEffect(() => {
    function applyTerritories(territories) {
      if (batchPrintEnabled && appState.focus === focusTypeName.Messages) {
        setTerritoryList(
          territories.filter((territory) =>
            batchPrintTerritories.includes(territory.id)
          )
        );
        return;
      }
      setTerritoryList(territories);
    }
    if (selectedMarket?.territories) {
      applyTerritories(selectedMarket.territories);
    }
  }, [selectedMarket, batchPrintEnabled, batchPrintTerritories, appState.focus]);

  return (
    <div className="page-content-with-sticky-actions">
      <CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.SELECTTERRITORY} focusType={appState?.focus} />

      <div className="container-xl mt-5">
        <div className="row justify-content-center mb-6">
          <div className="col-12 col-sm-10 col-lg-8">
            <h1 className="type-20 color-grey-d1 mb-5">
              {
                getConfigValue(
                  appState.config?.Territory?.SectionTitle,
                  appState
                )?.Value
              }
            </h1>
            <div className="card pd-card pt-3 pt-md-5 pb-4 pb-md-7">
              <div className="row justify-content-center">
                <div className="col-10 col-md-5">
                  <p>
                    {
                      getConfigValue(
                        multipleMarkets
                          ? appState.config?.Territory.SectionMessage
                          : appState.config?.Territory.SectionMessageTerritory,
                        appState
                      )?.Value
                    }
                  </p>
                  {multipleMarkets && (
                    <>
                      <label htmlFor="market" className="form-label">
                        {selectMarketConfigValue}
                      </label>
                      <Select
                        unstyled
                        classNames={{
                          control: () => "form-control",
                          menuList: () => "card",
                          option: (state) =>
                            state.isFocused
                              ? "bg-color-precision-grey text-white p-2"
                              : "p-2",
                        }}
                        aria-label={selectMarketConfigValue}
                        id="market"
                        type="search"
                        placeholder={selectMarketConfigValue}
                        onChange={handleMarket}
                        options={marketList}
                        getOptionLabel={(market) => market.name}
                        getOptionValue={(market) => market.id}
                        value={marketList?.filter(
                          (obj) => obj.id === selectedMarket?.id
                        )}
                      ></Select>
                    </>
                  )}

                  <div className="my-5"></div>
                  <label htmlFor="territory" className="form-label">
                    {selectTerritoryConfigValue}
                  </label>
                  <Select
                    unstyled
                    classNames={{
                      control: () => "form-control",
                      menuList: () => "card",
                      option: (state) =>
                        state.isFocused
                          ? "bg-color-precision-grey text-white p-2"
                          : "p-2",
                    }}
                    aria-label={selectTerritoryConfigValue}
                    id="territory"
                    type="search"
                    placeholder={selectTerritoryConfigValue}
                    onChange={handleTerritory}
                    options={territoryList}
                    getOptionLabel={(territory) => territory.name}
                    getOptionValue={(territory) => territory.id}
                    value={selectedMarket?.territories?.filter(
                      (obj) => obj.id === selectedTerritory?.id
                    )}
                  ></Select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={`${useStickyNavigation ? 'territory-button-bottom-container-sticky-low-z' : ''}`}>
          <div className='container-xl'>
              <div className='row justify-content-center justify-content-lg-between py-3'>
                  <div className='col-10 col-sm-8 col-md-6 col-xl-5'>
                      <div className='d-flex flex-column flex-lg-row'>
                          <div className='flex-fill w-100 ms-lg-3 pe-3'></div>
                      </div>
                  </div>
                  <div className='w-100 d-lg-none'></div>
                  <div className={`${useStickyNavigation ? 'col-12' : 'col-10'}`}>
                      <div className="d-flex flex-column flex-md-row-reverse" style={{ float: "right" }}>
                        {!selectedTerritory?.id && (
                          <Link
                            to="#"
                            className="btn btn-primary disabled btn-lg mb-lg-0 ms-lg-3 flex-fill w-100 ml-1"
                          >
                            {getConfigValue(appState.config?.Common?.Continue, appState)
                              ?.Value || null}
                          </Link>
                          )}
                          {selectedTerritory?.id && appState.focus === focusTypeName.HCPs && (
                            <Link
                              to="/HCPPage"
                              state={{ selectedTerritory }}
                              className="btn btn-primary btn-lg mb-lg-0 ms-lg-3 flex-fill w-100 ml-1"
                            >
                              {getConfigValue(appState.config?.Common?.Continue, appState)
                                ?.Value || null}
                            </Link>
                          )}
                          {selectedTerritory?.id && appState.focus === focusTypeName.Messages && (
                            <Link
                              to="/MessageSelectionPage"
                              state={{ selectedTerritory }}
                              className="btn btn-primary btn-lg mb-lg-0 ms-lg-3 flex-fill w-100 ml-1"
                            >
                              {getConfigValue(appState.config?.Common?.Continue, appState)
                                ?.Value || null}
                            </Link>
                          )}
                          {batchPrintEnabled && appState.focus === focusTypeName.Messages && (
                            <Link
                              to="/FocusPage"
                              className="btn btn-secondary btn-lg pe-7 flex-fill w-100"
                            >
                              {getConfigValue(appState.config?.Common?.Back, appState)
                                ?.Value || null}
                            </Link>
                          )}
                          {batchPrintEnabled && appState.focus === focusTypeName.HCPs && (
                            <Link
                              to="/FocusPage"
                              className="btn btn-secondary btn-lg pe-7 flex-fill w-100"
                            >
                              {getConfigValue(appState.config?.Common?.Back, appState)
                                ?.Value || null}
                            </Link>
                          )}
                          {!batchPrintEnabled && appState.focus === focusTypeName.HCPs && (
                            <Link
                              to="/RecentSalesAidsPage"
                              className="btn btn-secondary btn-lg pe-7 flex-fill w-100"
                            >
                              {getConfigValue(appState.config?.Common?.Back, appState)
                                ?.Value || null}
                            </Link>
                          )} 
                      </div>
                  </div>
              </div>
          </div>
      </div>
    </div>
  );
}

export default TerritoryPage;
